import React from 'react';
import { Messages } from '../../../../../../gen-src/Localize';
import { LabelButtonContext, RenderLabelAssetProps, LabelsPopoverContext, groupLabelsByType } from '../LabelHelpers';
import { ViewLineageButton } from '../LabelLineageButton';
import { ExpandoSectionBreakClass, LabelGroupHeadingDiv, LabelLabelsGroupDiv } from '../Styles';
import { onThisAssetMessage, inheritedByThisAssetMessage } from '../shared/LabelAssetContent';
import { ExpandoWidgetWrapper, LabelDetailAuthorElement, LabelDetailDateElement, LabelDetailMessageElement, TextLinkWidgetLabelDetailAsset } from '../shared/LabelPopoverDetail';
import { RenderParentedLabel, RenderUpstreamLabel } from '../shared/LabelRenderProps';
import { HydratedSensitivityRenderProps, RenderParentedSensitivity, RenderSensitivity, RenderUpstreamSensitivity } from './SensitivityProps';

export const LabelDetailChipId = 'data-quality-detail-chip-id';

function sortSensitivitiesByType(labels: readonly RenderSensitivity[]): RenderLabelAssetProps[][] {
  return groupLabelsByType(labels.map(mapSensitivityToLabelAssetProps));
}

function mapSensitivityToLabelAssetProps(label: RenderParentedSensitivity | RenderUpstreamSensitivity): RenderLabelAssetProps {
  const { content } = label;
  const asset = ('inheritedBy' in label && label.inheritedBy) ? label.inheritedBy : content;
  return ({
    subjectArea: asset?.subjectArea ?? null,
    assetName: asset?.contentName ?? null,
    assetType: asset?.contentType ?? null,
    assetUrl: asset?.contentUrl ?? null,
    remoteType: asset?.remoteType ?? content?.remoteType ?? undefined,
    parentName: ('parent' in label ? label.parent?.contentName : undefined),
    parentUrl: ('parent' in label ? label.parent?.contentUrl : undefined),
    category: 'sensitivity',
    labels: [label]
  });
}

function SensitivityExpandoSection(props: RenderLabelAssetProps & {
  readonly label: RenderSensitivity,
  readonly collapseDetails: boolean,
}): JSX.Element | null {
  const { linksDisabled } = React.useContext(LabelButtonContext);
  const { category, content: baseContent } = React.useContext(LabelsPopoverContext);
  const { assetName, assetType, assetUrl, remoteType, subjectArea, label } = props;

  return (
    <LabelLabelsGroupDiv>
      <TextLinkWidgetLabelDetailAsset
        assetName={assetName ?? baseContent?.contentName ?? null}
        assetType={assetType ?? baseContent?.contentType ?? null}
        assetUrl={linksDisabled ? null : (assetUrl ?? baseContent?.contentUrl ?? null)}
        remoteType={remoteType ?? baseContent?.remoteType}
        subjectArea={subjectArea ?? null}
      />
      {
        props.collapseDetails ? null :
          <>
            <LabelDetailAuthorElement {...label} category={category} />
            <LabelDetailDateElement {...label} />
            <LabelDetailMessageElement {...label} assetName={assetName ?? baseContent?.contentName ?? null} />
          </>

      }
    </LabelLabelsGroupDiv>
  )
}

function RenderSensitivities(props: RenderLabelAssetProps & { readonly collapseDetails: boolean, readonly keyIndex?: string; }): JSX.Element {
  const { labels } = props;
  return (
    <React.Fragment key={props.keyIndex}>
      {
        labels.map((i: RenderSensitivity, index: React.Key | undefined) =>
          <React.Fragment key={index}>
            <SensitivityExpandoSection {...props} label={i} />
          </React.Fragment>
        )
      }
    </React.Fragment>
  )
}

function SensitivityAnchoredSection({ anchored, ...props }: Omit<HydratedSensitivityRenderProps, 'parented' | 'upstream'>): JSX.Element | null {
  if (!anchored || anchored.labels.length < 1) {
    return null;
  }

  const sensitivities: RenderLabelAssetProps[][] = sortSensitivitiesByType(anchored?.labels);
  const { contentType } = props.content;

  return (
    <>
      <LabelGroupHeadingDiv>
        {onThisAssetMessage(contentType)}
      </LabelGroupHeadingDiv>
      {sensitivities.map((c, j) =>
        <ExpandoWidgetWrapper
          length={c.length}
          labelType={c[0].labels[0].type}
          overrides={{ collapse: 'disallow' }}
          key={j}
        >
          {c.map((x, i) =>
            <React.Fragment key={i}>
              <RenderSensitivities {...x} collapseDetails={false} keyIndex={`${i}`} />
              <LabelLabelsGroupDiv>
                {(c.length > 1 && i < c.length - 1)?  <hr className={ExpandoSectionBreakClass}/> : null}
              </LabelLabelsGroupDiv>
            </React.Fragment>
          )}
        </ExpandoWidgetWrapper>)
      }
    </>
  )
}

function RenderParentedSensitivities(props: { labels: RenderLabelAssetProps[], upstream: boolean } & { readonly keyIndex?: string; }): JSX.Element {
  const sortedLabels = props.labels.sort((a, b) => (a.assetName ?? '').localeCompare(b.assetName ?? ''));

  return (
    <>
      {
        sortedLabels.map((x, i) => {
          return (
            <React.Fragment key={i}>
              <RenderSensitivities {...x} collapseDetails={true} keyIndex={`${i}`} />
            </React.Fragment>)
        })
      }
    </>
  )
}

function RenderNonParentedUpstreamSection(props: { labels: readonly RenderUpstreamSensitivity[], readonly message: string }): JSX.Element | null {
  if (!props.labels || props.labels.length < 1) {
    return null;
  }
  const sensitivities: RenderLabelAssetProps[][] = sortSensitivitiesByType(props.labels);
  return (
    <>
      <LabelGroupHeadingDiv>
        {props.message}
      </LabelGroupHeadingDiv>
      {sensitivities.map((c, j) =>
        <ExpandoWidgetWrapper
          length={c.length}
          labelType={c[0].labels[0].type}
          key={j}
        >
          {c.map((x, i) =>
            <React.Fragment key={i}>
              <RenderSensitivities  {...x} category={'sensitivity'} labels={x.labels} collapseDetails={false} keyIndex={`${i}`} />
              <LabelLabelsGroupDiv>
                {(c.length > 1 && i < c.length - 1)?  <hr className={ExpandoSectionBreakClass}/> : null}
              </LabelLabelsGroupDiv>
            </React.Fragment>
          )}
        </ExpandoWidgetWrapper>
      )}
    </>
  );
}

function RenderInheritedAndOrParentedSection(props: { inheritedColumnSensitivities: readonly RenderUpstreamLabel[], directParentedSensitivities: readonly RenderParentedLabel[] }): JSX.Element | null {
  const inheritedAndParentedSensitivities = props.inheritedColumnSensitivities.concat(props.directParentedSensitivities);
  if (inheritedAndParentedSensitivities.length < 1) {
    return null;
  }

  const asset = inheritedAndParentedSensitivities[0].inheritedBy ?? inheritedAndParentedSensitivities[0].content;
  const sensitivities: RenderLabelAssetProps[][] = sortSensitivitiesByType(inheritedAndParentedSensitivities);

  return (
    <>
      <LabelGroupHeadingDiv>
        {props.inheritedColumnSensitivities.length > 0 ?
          <>
            <span>{inheritedByThisAssetMessage(asset.contentType ?? asset.subjectArea ?? null, inheritedAndParentedSensitivities.length)}</span>
            <ViewLineageButton contentType={asset.contentType ?? asset.subjectArea ?? null} />
          </>
          : (props.directParentedSensitivities.length > 0 ? Messages.Label_direct_columns({ MESSAGE_COUNT: props.directParentedSensitivities.length }) : null)}
      </LabelGroupHeadingDiv>
      <>
        {
          sensitivities.map((c, i) => {
            return (
              <ExpandoWidgetWrapper
                length={c.length}
                labelType={c[0].labels[0].type}
                key={i}
              >
                <RenderParentedSensitivities labels={c} upstream={true} key={i} keyIndex={`${i}`} />
              </ExpandoWidgetWrapper>
            )
          })
        }
      </>
    </>
  )
}

function SensitivityUpstreamAndParentedSection({ upstream, parented, ...props }: Omit<HydratedSensitivityRenderProps, 'anchored'>): JSX.Element | null {
  const inheritedColumnSensitivities = upstream?.labels.filter(i => i.inheritedBy) ?? [];
  const directInheritedSensitivities = upstream?.labels.filter(i => !(i.inheritedBy)) ?? [];
  const directParentedSensitivities = parented ? parented.labels : [];
  return (
    <>
      {(directInheritedSensitivities.length > 0) && <div style={{ height: '10px' }}></div>}
      <RenderNonParentedUpstreamSection labels={directInheritedSensitivities} message={inheritedByThisAssetMessage(props.content.subjectArea ?? null, directInheritedSensitivities.length)} />
      {(inheritedColumnSensitivities.length > 0 || directParentedSensitivities.length > 0) && <div style={{ height: '10px' }}></div>}
      <RenderInheritedAndOrParentedSection inheritedColumnSensitivities={inheritedColumnSensitivities} directParentedSensitivities={directParentedSensitivities} />
    </>
  )
}

export function SensitivityPopover(props: HydratedSensitivityRenderProps): JSX.Element {
  const anchoredSection = SensitivityAnchoredSection(props);
  const upstreamAndParentedSection = SensitivityUpstreamAndParentedSection(props);

  if (!anchoredSection && !upstreamAndParentedSection) {
    return (<></>);
  }

  return (
    <>
      {anchoredSection && anchoredSection}
      {upstreamAndParentedSection && upstreamAndParentedSection}
    </>
  );
}
